import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { ImageField } from "./common";

const EMPTY = { name: "", description: "", image: "", published: true };

export default function CategoriesAdmin() {
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const openEditor = (c) => {
        setEditing(c || null);
        setForm(c ? { name: c.name, description: c.description || "", image: c.image || "", published: c.published !== false } : EMPTY);
        setOpen(true);
    };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/admin/categories/${editing.id}`, form);
                toast.success("Category updated");
            } else {
                await api.post("/admin/categories", form);
                toast.success("Category added");
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const move = async (index, dir) => {
        const ids = categories.map((c) => c.id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + dir, 0, moved);
        setCategories(ids.map((id) => categories.find((c) => c.id === id)));
        await api.post("/admin/categories/reorder", { ids }).catch(() => toast.error("Reorder failed"));
    };

    return (
        <div data-testid="admin-categories">
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-slate-500" data-testid="admin-categories-count">
                    {categories.length} categories — customers filter products by these
                </p>
                <Button onClick={() => openEditor(null)} data-testid="admin-add-category" className="rounded-full bg-[#0033A0] hover:bg-[#002277]">
                    <Plus className="h-4 w-4 mr-2" /> Add Category
                </Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="admin-categories-list">
                {categories.map((c, i) => (
                    <div key={c.id} className="flex items-center gap-4 p-4" data-testid={`admin-category-row-${c.id}`}>
                        <div className="flex flex-col">
                            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up"
                                data-testid={`cat-up-${c.id}`} className="text-slate-400 hover:text-[#0033A0] disabled:opacity-20">
                                <ArrowUp className="h-4 w-4" />
                            </button>
                            <button type="button" disabled={i === categories.length - 1} onClick={() => move(i, 1)} aria-label="Move down"
                                data-testid={`cat-down-${c.id}`} className="text-slate-400 hover:text-[#0033A0] disabled:opacity-20">
                                <ArrowDown className="h-4 w-4" />
                            </button>
                        </div>
                        <img src={c.image} alt="" className="h-14 w-14 rounded-lg object-cover bg-slate-100 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="font-heading font-bold text-slate-900 truncate flex items-center gap-2">
                                {c.name}
                                {c.published === false && <span className="rounded-full bg-slate-200 text-slate-600 text-xs px-2 py-0.5">Hidden</span>}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{c.description}</p>
                        </div>
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEditor(c)} data-testid={`admin-edit-category-${c.id}`}>
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`admin-delete-category-${c.id}`}
                            onClick={() => window.confirm(`Delete ${c.name}? Products in it will keep the category name.`) &&
                                api.delete(`/admin/categories/${c.id}`).then(load).then(() => toast.success("Deleted"))}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="admin-category-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Category" : "Add Category"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="cf-name">Name *</Label>
                            <Input id="cf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cf-name" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="cf-desc">Description</Label>
                            <Textarea id="cf-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cf-description" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Image</Label>
                            <ImageField value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="cf-image" />
                        </div>
                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                                className="accent-[#0033A0]" data-testid="cf-published" />
                            Published (visible in shop)
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                        <Button onClick={save} disabled={saving} className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="cf-save">
                            {saving ? "Saving…" : "Save Category"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
